import type {
  ApiResponse,
  Customer,
  CustomerWithBalance,
  Product,
  ProductWithPrice,
  ProductDeliveryOption,
  Invoice,
  InvoiceWithItems,
  Payment,
  Inventory,
  AuthUser,
  Quotation,
  QuotationWithItems,
} from '@fieldforce/shared';
import { getSubdomain } from './subdomain';

// Production API URL - hard-coded for deployed builds
const PRODUCTION_API_URL = 'https://fieldforce-api.muhsinam77.workers.dev/api';

// Get API URL
function getApiUrl(): string {
  // Check for explicit env var first (for local development override)
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  // For local dev, set VITE_API_BASE_URL=http://127.0.0.1:8787/api in .env.local
  // Otherwise use production URL
  return PRODUCTION_API_URL;
}

// Storage keys
const AUTH_TOKEN_KEY = 'auth_token';
const AUTH_USER_KEY = 'auth_user';

/**
 * Get stored auth token
 */
export function getAuthToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

/**
 * Get stored user
 */
export function getStoredUser(): AuthUser | null {
  const user = localStorage.getItem(AUTH_USER_KEY);
  return user ? JSON.parse(user) : null;
}

/**
 * Store auth data
 */
export function setAuthData(token: string, user: AuthUser): void {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
}

/**
 * Clear auth data
 */
export function clearAuthData(): void {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
}

/**
 * Internal API request helper
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const token = getAuthToken();
  const subdomain = getSubdomain();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  // Add auth token if available
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Add tenant header (required for API to identify tenant)
  if (subdomain) {
    headers['X-Tenant-ID'] = subdomain;
  }

  const url = `${getApiUrl()}${endpoint}`;

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || { code: 'UNKNOWN_ERROR', message: 'An error occurred' },
      };
    }

    return data;
  } catch (error) {
    console.error('API request failed:', error);
    return {
      success: false,
      error: { code: 'NETWORK_ERROR', message: 'Network error. Please check your connection.' },
    };
  }
}

// ============================================
// AUTH API
// ============================================

export const authApi = {
  login: async (mobile: string, password: string) => {
    const result = await apiRequest<{ token: string; user: AuthUser }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ mobile, password }),
    });

    if (result.success && result.data) {
      setAuthData(result.data.token, result.data.user);
    }

    return result;
  },

  logout: async () => {
    await apiRequest('/auth/logout', { method: 'POST' });
    clearAuthData();
  },

  verify: async () => {
    return apiRequest<{ user: AuthUser }>('/auth/verify');
  },

  me: async () => {
    return apiRequest<AuthUser>('/auth/me');
  },

  branding: async () => {
    return apiRequest<{
      tenant_id: string;
      name: string;
      subdomain: string;
      logo_url: string | null;
      settings: Record<string, unknown>;
    }>('/auth/branding');
  },

  changePassword: async (currentPassword: string, newPassword: string) => {
    return apiRequest<void>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  },
};

// ============================================
// CUSTOMERS API
// ============================================

export const customersApi = {
  getAll: async () => {
    return apiRequest<Customer[]>('/customers');
  },

  getById: async (id: string) => {
    return apiRequest<CustomerWithBalance>(`/customers/get?id=${id}`);
  },

  search: async (query: string) => {
    return apiRequest<Customer[]>(`/customers/search?q=${encodeURIComponent(query)}`);
  },

  getBalance: async (customerId: string) => {
    return apiRequest<{
      customer_id: string;
      customer_name: string;
      credit_limit: number;
      total_outstanding: number;
      available_credit: number;
      unpaid_invoices: Array<Invoice & { balance: number }>;
    }>(`/customers/${customerId}/balance`);
  },

  create: (data: unknown) => apiRequest('/customers/create', { method: 'POST', body: JSON.stringify(data) }),
  update: (data: unknown) => apiRequest('/customers/update', { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => apiRequest(`/customers/delete?id=${id}`, { method: 'DELETE' }),
};

// ============================================
// PRODUCTS API
// ============================================

export const productsApi = {
  getAll: async () => {
    return apiRequest<Product[]>('/products');
  },

  getById: async (id: string) => {
    return apiRequest<Product>(`/products/get?id=${id}`);
  },

  getPricing: async (customerId: string) => {
    return apiRequest<ProductWithPrice[]>(`/products/pricing?customerId=${customerId}`);
  },

  getDeliveryOptions: async (customerId: string) => {
    return apiRequest<ProductDeliveryOption[]>(`/products/delivery-options?customerId=${customerId}`);
  },

  setCustomerPrice: async (data: { customer_id: string; product_id: string; price: number }) =>
    apiRequest('/products/pricing', { method: 'POST', body: JSON.stringify(data) }),

  removeCustomerPrice: async (customerId: string, productId: string) =>
    apiRequest(`/products/pricing?customerId=${customerId}&productId=${productId}`, { method: 'DELETE' }),

  create: (data: unknown) => apiRequest('/products/create', { method: 'POST', body: JSON.stringify(data) }),
  update: (data: unknown) => apiRequest('/products/update', { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => apiRequest(`/products/delete?id=${id}`, { method: 'DELETE' }),
};

// ============================================
// INVOICES API
// ============================================

export const invoicesApi = {
  getAll: async (params?: { status?: string; customer_id?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.customer_id) searchParams.set('customer_id', params.customer_id);
    const query = searchParams.toString();
    return apiRequest<Invoice[]>(`/invoices${query ? `?${query}` : ''}`);
  },

  getById: async (id: string) => {
    return apiRequest<InvoiceWithItems>(`/invoices/get?id=${id}`);
  },

  create: async (data: {
    customer_id: string;
    items: Array<{ product_id: string; quantity: number; unit_price: number }>;
    notes?: string;
  }) => {
    return apiRequest<{ invoice_id: string; invoice_number: string; total_amount: number }>(
      '/invoices/create',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  },

  getByCustomer: async (customerId: string) => {
    return apiRequest<Invoice[]>(`/invoices/customer/${customerId}`);
  },

  getPending: async (customerId: string) => {
    return apiRequest<Array<Invoice & { balance: number }>>(`/invoices/pending/${customerId}`);
  },

  getHistory: async (invoiceId: string) => {
    return apiRequest<Array<{
      log_id: string;
      user_id: string | null;
      action: string;
      changes: Record<string, unknown> | null;
      created_at: string;
      user_name: string | null;
    }>>(`/invoices/${invoiceId}/history`);
  },

  getAllHistory: async () => {
    return apiRequest<Array<{
      log_id: string;
      entity_id: string;
      user_id: string | null;
      action: string;
      changes: Record<string, unknown> | null;
      created_at: string;
      user_name: string | null;
      invoice_number: string | null;
      customer_name: string | null;
    }>>('/invoices/history');
  },

  void: (invoiceId: string, reason?: string) => apiRequest('/invoices/void', { method: 'PUT', body: JSON.stringify({ invoice_id: invoiceId, reason }) }),

  update: (data: {
    invoice_id: string;
    items?: Array<{ product_id: string; quantity: number; unit_price: number }>;
    notes?: string | null;
    reason: string;
  }) => apiRequest('/invoices/update', { method: 'PUT', body: JSON.stringify(data) }),

  adminCreate: async (data: {
    customer_id: string;
    items: Array<{ product_id: string; quantity: number; unit_price: number }>;
    date?: string;
    notes?: string;
    deduct_inventory?: boolean;
  }) => {
    return apiRequest<{ invoice_id: string; invoice_number: string; total_amount: number }>(
      '/invoices/admin-create',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  },
};

// ============================================
// QUOTATIONS API
// ============================================

export const quotationsApi = {
  getAll: async (params?: { status?: string; customer_id?: string; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.customer_id) searchParams.set('customer_id', params.customer_id);
    if (params?.search) searchParams.set('search', params.search);
    const query = searchParams.toString();
    return apiRequest<Quotation[]>(`/quotations${query ? `?${query}` : ''}`);
  },

  getById: async (id: string) => {
    return apiRequest<QuotationWithItems>(`/quotations/get?id=${id}`);
  },

  create: async (data: {
    customer_id: string;
    items: Array<{ product_id: string; quantity: number; unit_price: number }>;
    date?: string;
    valid_until?: string;
    notes?: string;
  }) => {
    return apiRequest<{ quotation_id: string; quotation_number: string; total_amount: number }>(
      '/quotations/create',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  },

  update: async (data: {
    quotation_id: string;
    items?: Array<{ product_id: string; quantity: number; unit_price: number }>;
    valid_until?: string;
    notes?: string | null;
    status?: 'draft' | 'confirmed';
  }) => {
    return apiRequest<{ total_amount: number }>('/quotations/update', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (id: string) => {
    return apiRequest(`/quotations/delete?id=${id}`, { method: 'DELETE' });
  },

  convert: async (quotationId: string) => {
    return apiRequest<{ invoice_id: string; invoice_number: string; total_amount: number }>(
      '/quotations/convert',
      {
        method: 'POST',
        body: JSON.stringify({ quotation_id: quotationId }),
      }
    );
  },
};

// ============================================
// PAYMENTS API
// ============================================

export const paymentsApi = {
  getAll: async (params?: { customer_id?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.customer_id) searchParams.set('customer_id', params.customer_id);
    const query = searchParams.toString();
    return apiRequest<Payment[]>(`/payments${query ? `?${query}` : ''}`);
  },

  getById: async (id: string) => {
    return apiRequest<Payment>(`/payments/get?id=${id}`);
  },

  create: async (data: {
    customer_id: string;
    amount: number;
    payment_method: 'cash' | 'cheque' | 'transfer';
    invoice_ids?: string[];
    notes?: string;
  }) => {
    return apiRequest<{
      payment_id: string;
      amount: number;
      applied_amount: number;
      unapplied_amount: number;
      applications: Array<{ invoice_id: string; amount: number }>;
    }>('/payments/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getByCustomer: async (customerId: string) => {
    return apiRequest<Payment[]>(`/payments/customer/${customerId}`);
  },
};

// ============================================
// INVENTORY API
// ============================================

export const inventoryApi = {
  getAll: () => apiRequest('/inventory'),
  getWarehouse: () => apiRequest('/inventory/warehouse'),
  getStaffInventory: (staffId: string) => apiRequest(`/inventory/staff/${staffId}`),
  getMyInventory: async () => {
    return apiRequest<Array<Inventory & { product_name: string; product_sku: string | null }>>(
      '/inventory'
    );
  },
  assign: (data: { staff_id: string; product_id: string; quantity: number; notes?: string }) => apiRequest('/inventory/assign', { method: 'POST', body: JSON.stringify(data) }),
  return: (data: { staff_id: string; product_id: string; quantity: number; notes?: string }) => apiRequest('/inventory/return', { method: 'POST', body: JSON.stringify(data) }),
  dispose: (data: { staff_id: string; product_id: string; quantity: number; notes?: string }) => apiRequest('/inventory/dispose', { method: 'POST', body: JSON.stringify(data) }),
  adjust: (data: { product_id: string; location_type: string; location_id?: string; quantity: number; notes?: string }) => apiRequest('/inventory/adjust', { method: 'POST', body: JSON.stringify(data) }),
  receive: (data: { product_id: string; quantity: number; notes?: string }) => apiRequest('/inventory/receive', { method: 'POST', body: JSON.stringify(data) }),
  getMovements: () => apiRequest('/inventory/movements'),
  getLowStock: (threshold?: number) => apiRequest(`/inventory/low-stock${threshold ? `?threshold=${threshold}` : ''}`),
};

// ============================================
// ROUTES API
// ============================================

export const routesApi = {
  getAll: () => apiRequest('/routes'),
  getById: (id: string) => apiRequest(`/routes/get?id=${id}`),
  create: (data: unknown) => apiRequest('/routes/create', { method: 'POST', body: JSON.stringify(data) }),
  update: (data: unknown) => apiRequest('/routes/update', { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => apiRequest(`/routes/delete?id=${id}`, { method: 'DELETE' }),
  assignStaff: (routeId: string, staffIds: string[]) => apiRequest('/routes/assign-staff', { method: 'POST', body: JSON.stringify({ route_id: routeId, staff_ids: staffIds }) }),
};

// ============================================
// USERS API
// ============================================

export const usersApi = {
  getAll: () => apiRequest('/users'),
  getById: (id: string) => apiRequest(`/users/get?id=${id}`),
  create: (data: unknown) => apiRequest('/users/create', { method: 'POST', body: JSON.stringify(data) }),
  update: (data: { userId: string; name?: string; email?: string; phone?: string; role?: string; is_active?: number }) =>
    apiRequest('/users/update', { method: 'PUT', body: JSON.stringify({ user_id: data.userId, name: data.name, email: data.email, phone: data.phone, role: data.role, is_active: data.is_active }) }),
  delete: (id: string) => apiRequest(`/users/delete?id=${id}`, { method: 'DELETE' }),
  resetPassword: (data: { userId: string; newPassword: string }) => apiRequest('/users/reset-password', { method: 'POST', body: JSON.stringify({ user_id: data.userId, new_password: data.newPassword }) }),
  getPerformance: (userId: string, period?: string) =>
    apiRequest(`/users/${userId}/performance${period ? `?period=${period}` : ''}`),
};

// ============================================
// EXPENSES API
// ============================================

export interface Expense {
  expense_id: string;
  tenant_id: string;
  category: string;
  amount: number;
  description: string | null;
  date: string;
  payee: string | null;
  payment_method: string | null;
  notes: string | null;
  recurring_expense_id: string | null;
  created_at: string;
  created_by: string | null;
}

export interface RecurringExpense {
  recurring_id: string;
  tenant_id: string;
  category: string | null;
  description: string | null;
  total_amount: number;
  per_period_amount: number;
  frequency: 'weekly' | 'monthly' | 'annually';
  day_of_month: number | null;
  day_of_week: number | null;
  payee: string | null;
  payment_method: string | null;
  notes: string | null;
  start_date: string;
  end_date: string | null;
  is_active: number;
  last_generated_date: string | null;
  created_at: string;
  created_by: string | null;
}

export const expensesApi = {
  getAll: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return apiRequest<Expense[]>(`/expenses${query}`);
  },
  getById: (id: string) => apiRequest<Expense>(`/expenses/get?id=${id}`),
  create: (data: {
    category: string;
    amount: number;
    description?: string;
    date: string;
    payee?: string;
    payment_method?: string;
    notes?: string;
    recurring_expense_id?: string;
  }) => apiRequest<{ expense_id: string }>('/expenses/create', { method: 'POST', body: JSON.stringify(data) }),
  update: (data: {
    expense_id: string;
    category?: string;
    amount?: number;
    description?: string | null;
    date?: string;
    payee?: string | null;
    payment_method?: string | null;
    notes?: string | null;
  }) => apiRequest('/expenses/update', { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => apiRequest(`/expenses/delete?id=${id}`, { method: 'DELETE' }),
  getCategories: () => apiRequest<string[]>('/expenses/categories'),
  getSummary: (startDate: string, endDate: string) => apiRequest<{
    start_date: string;
    end_date: string;
    total: number;
    count: number;
    by_category: Array<{ category: string; total: number; count: number }>;
  }>(`/expenses/summary?start_date=${startDate}&end_date=${endDate}`),
};

// ============================================
// RECURRING EXPENSES API
// ============================================

export const recurringExpensesApi = {
  getAll: (includeInactive = false) => {
    const query = includeInactive ? '?include_inactive=true' : '';
    return apiRequest<RecurringExpense[]>(`/recurring-expenses${query}`);
  },
  getById: (id: string) => apiRequest<RecurringExpense>(`/recurring-expenses/get?id=${id}`),
  create: (data: {
    category?: string;
    description?: string;
    total_amount?: number;
    per_period_amount?: number;
    frequency: 'weekly' | 'monthly' | 'annually';
    day_of_month?: number;
    day_of_week?: number;
    payee?: string;
    payment_method?: string;
    notes?: string;
    start_date: string;
    end_date?: string;
  }) => apiRequest<{ recurring_id: string }>('/recurring-expenses/create', { method: 'POST', body: JSON.stringify(data) }),
  update: (data: {
    recurring_id: string;
    category?: string | null;
    description?: string | null;
    total_amount?: number;
    per_period_amount?: number;
    frequency?: 'weekly' | 'monthly' | 'annually';
    day_of_month?: number | null;
    day_of_week?: number | null;
    payee?: string | null;
    payment_method?: string | null;
    notes?: string | null;
    start_date?: string;
    end_date?: string | null;
    is_active?: number;
  }) => apiRequest('/recurring-expenses/update', { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => apiRequest(`/recurring-expenses/delete?id=${id}`, { method: 'DELETE' }),
  generate: () => apiRequest<{ generated_count: number; expense_ids: string[] }>('/recurring-expenses/generate', { method: 'POST' }),
};

// ============================================
// REPORTS API
// ============================================

export const reportsApi = {
  getDashboard: (period?: 'today' | 'week' | 'month' | 'quarter' | 'year', staffId?: string) => {
    const params = new URLSearchParams();
    if (period) params.set('period', period);
    if (staffId) params.set('staff_id', staffId);
    const query = params.toString();
    return apiRequest(`/reports/dashboard${query ? `?${query}` : ''}`);
  },
  getSales: (params: { startDate: string; endDate: string }) => apiRequest(`/reports/sales?start_date=${params.startDate}&end_date=${params.endDate}`),
  getCollections: (params: { startDate: string; endDate: string }) => apiRequest(`/reports/collections?start_date=${params.startDate}&end_date=${params.endDate}`),
  getOutstanding: () => apiRequest('/reports/outstanding'),
  getStaffPerformance: (params: { startDate: string; endDate: string }) => apiRequest(`/reports/staff-performance?start_date=${params.startDate}&end_date=${params.endDate}`),
  getInventory: () => apiRequest('/reports/inventory'),
  getProfitLoss: (params: { startDate: string; endDate: string }) => apiRequest(`/reports/profit-loss?start_date=${params.startDate}&end_date=${params.endDate}`),
};

// ============================================
// SETTINGS API
// ============================================

export interface TenantSettings {
  // Company Profile
  company_name?: string;
  company_trn?: string;
  company_phone?: string;
  company_email?: string;
  company_website?: string;
  address_line_1?: string;
  address_line_2?: string;
  address_line_3?: string;
  pdf_logo_base64?: string;
  pdf_footer_text?: string;

  // Document Numbering
  invoice_prefix?: string;
  invoice_starting?: number;
  invoice_padding?: number;
  receipt_prefix?: string;
  receipt_starting?: number;
  receipt_padding?: number;
  quotation_prefix?: string;
  quotation_starting?: number;
  quotation_padding?: number;
  customer_id_prefix?: string;
  customer_id_starting?: number;
  customer_id_padding?: number;

  // Defaults
  default_invoice_notes?: string;
  default_quotation_notes?: string;

  // Validity & Due Dates
  quote_validity_days?: number;
  invoice_due_days?: number;

  // Thank You Messages
  invoice_thank_you?: string;
  quotation_thank_you?: string;
  receipt_thank_you?: string;
}

export const settingsApi = {
  get: () =>
    apiRequest<{
      tenant_id: string;
      name: string;
      subdomain: string;
      logo_url: string | null;
      settings: TenantSettings;
    }>('/settings'),

  save: (settings: TenantSettings) =>
    apiRequest<{ settings: TenantSettings }>('/settings', {
      method: 'PUT',
      body: JSON.stringify({ settings }),
    }),
};
